// @ts-nocheck
import {
  BUTTON_MENU_ACTIVE_BUTTON_SELECTOR,
  BUTTON_MENU_BUTTON_GAP_PX,
  BUTTON_MENU_BUTTON_WIDTH,
  BUTTON_MENU_ERROR_BUTTON_SELECTOR,
  BUTTON_MENU_SCROLL_EDGE_TOLERANCE,
} from './constants';

import type { SxProps, Theme } from '@mui/material';
import type { ScrollState } from './types';

export type MenuRouteLocation = {
  pathname: string;
  search?: string;
  hash?: string;
};

function normalizePathname(pathname: string): string {
  if (pathname === '/') {
    return pathname;
  }

  return pathname.replace(/\/+$/, '');
}

function hasMatchingSearchAndHash(
  currentLocation: MenuRouteLocation,
  optionLocation: MenuRouteLocation,
): boolean {
  const optionHasSearch = optionLocation.search != null && optionLocation.search !== '';
  const optionHasHash = optionLocation.hash != null && optionLocation.hash !== '';

  return (
    (!optionHasSearch || currentLocation.search === optionLocation.search) &&
    (!optionHasHash || currentLocation.hash === optionLocation.hash)
  );
}

export function isMenuOptionActive(
  currentLocation: MenuRouteLocation,
  optionLocation: MenuRouteLocation,
  end = true,
): boolean {
  const normalizedCurrentPathname = normalizePathname(currentLocation.pathname);
  const normalizedOptionPathname = normalizePathname(optionLocation.pathname);

  if (end) {
    return (
      normalizedCurrentPathname === normalizedOptionPathname &&
      hasMatchingSearchAndHash(currentLocation, optionLocation)
    );
  }

  return (
    (normalizedCurrentPathname === normalizedOptionPathname ||
      normalizedCurrentPathname.startsWith(`${normalizedOptionPathname}/`)) &&
    hasMatchingSearchAndHash(currentLocation, optionLocation)
  );
}

// Rendered desktop width of the button row: fixed-width buttons plus the gaps between them.
export function getButtonMenuWidth(optionCount: number): number {
  return (
    optionCount * BUTTON_MENU_BUTTON_WIDTH + Math.max(0, optionCount - 1) * BUTTON_MENU_BUTTON_GAP_PX
  );
}

export function mergeSxProps(
  baseSx: SxProps<Theme>,
  userSx: SxProps<Theme> | undefined,
): SxProps<Theme> {
  if (userSx == null) {
    return baseSx;
  }

  return [baseSx, userSx];
}

export function getHiddenErrorState(
  scrollContainer: HTMLDivElement,
): Pick<ScrollState, 'hasHiddenLeftError' | 'hasHiddenRightError'> {
  const containerRect = scrollContainer.getBoundingClientRect();
  const errorButtons = scrollContainer.querySelectorAll<HTMLButtonElement>(
    BUTTON_MENU_ERROR_BUTTON_SELECTOR,
  );
  let hasHiddenLeftError = false;
  let hasHiddenRightError = false;

  errorButtons.forEach((button) => {
    const buttonRect = button.getBoundingClientRect();

    if (containerRect.width > 0) {
      hasHiddenLeftError =
        hasHiddenLeftError ||
        buttonRect.left < containerRect.left - BUTTON_MENU_SCROLL_EDGE_TOLERANCE;
      hasHiddenRightError =
        hasHiddenRightError ||
        buttonRect.right > containerRect.right + BUTTON_MENU_SCROLL_EDGE_TOLERANCE;
      return;
    }

    const visibleLeft = scrollContainer.scrollLeft - BUTTON_MENU_SCROLL_EDGE_TOLERANCE;
    const visibleRight =
      scrollContainer.scrollLeft + scrollContainer.clientWidth + BUTTON_MENU_SCROLL_EDGE_TOLERANCE;
    const buttonLeft = button.offsetLeft;
    const buttonRight = buttonLeft + button.offsetWidth;

    hasHiddenLeftError = hasHiddenLeftError || buttonLeft < visibleLeft;
    hasHiddenRightError = hasHiddenRightError || buttonRight > visibleRight;
  });

  return { hasHiddenLeftError, hasHiddenRightError };
}

// Reveal the active button by scrolling only the horizontal tab strip. Using
// Element.scrollIntoView here would bubble up to every scrollable ancestor —
// including the vertical page container — and yank the page to the top whenever
// the options change (e.g. tab statuses updating after an add/delete).
export function scrollActiveButtonIntoView(scrollContainer: HTMLDivElement): void {
  const activeButton = scrollContainer.querySelector<HTMLButtonElement>(
    BUTTON_MENU_ACTIVE_BUTTON_SELECTOR,
  );

  if (activeButton == null) {
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const buttonRect = activeButton.getBoundingClientRect();

  let left = 0;
  if (buttonRect.left < containerRect.left) {
    left = buttonRect.left - containerRect.left;
  } else if (buttonRect.right > containerRect.right) {
    left = buttonRect.right - containerRect.right;
  }

  if (left !== 0) {
    scrollContainer.scrollBy({ left, behavior: 'smooth' });
  }
}
